import axios from 'axios';
import { FormEvent, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useConfig } from '../../config';
import { useTranslation } from '../../i18n/i18n';
import { Res, useCms } from '../../state/api';
import { MessagesContainer } from '../../state/messages';
import { ApiActionResponse } from '../../types';
import { AlertMessages, useAlerts } from '../AlertMessages/AlertMessages';
import { CmsComponent } from '../cms/CmsComponent';
import { ContentCard } from '../ContentCard/ContentCard';
import { Button } from '../Form/Button';
import { InputGrid } from '../Form/InputGrid';
import { NewPasswordInput, NewPasswordModel } from '../Form/NewPasswordInput';
import { onKeyPress } from '../Form/PressedEnter';
import { Main } from '../Main/Main';
import { Routing } from '../Routes';

type UrlParams = { customerId: string; code: string };

export const ResetPasswordPage = (): JSX.Element => {
	const { api } = useConfig();
	const { data: resetPasswordSuccess } = useCms(Res.cmsComponent, { slug: 'reset-password-success' });
	const { customerId, code } = useParams<UrlParams>();
	const t = useTranslation().resetPassword;
	const alert = MessagesContainer.useContainer();
	const [validCode, setValidCode] = useState(false);
	const [successMsg, setSuccessMsg] = useState(false);
	const [passwordModel, setPasswordModel] = useState<NewPasswordModel>({
		newPassword: '',
		newPassword2: '',
		isValid: false,
	});
	const alerts = useAlerts();

	useEffect(() => {
		verifyCode();
	}, []); // eslint-disable-line react-hooks/exhaustive-deps

	async function verifyCode() {
		const payload = {
			code: code,
		};
		try {
			const res = await axios.post<ApiActionResponse>(`${api}reset-password-code.json`, payload);
			if (res.data.success) {
				setValidCode(true);
			} else {
				throw new Error();
			}
		} catch (e) {
			alert.set({ type: 'error', message: t.linkIsNotValid });
		}
	}

	const handleFormSubmit = async (event: FormEvent<HTMLFormElement>) => {
		event.preventDefault();

		const payload = {
			customerId: customerId,
			code: code,
			password: passwordModel.newPassword,
			confirm: passwordModel.newPassword2,
		};
		const res = await axios.post<ApiActionResponse>(`${api}reset-password.json`, payload);
		if (res.data.errors) {
			alerts.set({ type: 'error', message: res.data.errors.join(' ') });
		} else if (res.data.success) {
			setSuccessMsg(true);
		} else {
			alerts.set({ type: 'error', message: t.linkIsNotValid });
		}
	};

	return (
		<Main>
			{validCode && !successMsg ? (
				<>
					<h1>{t.resetPassword}</h1>
					<ContentCard>
						<AlertMessages alerts={alerts} />
						<form onSubmit={handleFormSubmit}>
							<InputGrid>
								<NewPasswordInput
									fullWidth
									model={passwordModel}
									onChange={(data) => setPasswordModel(data)}
									onKeyPress={onKeyPress}
								/>
							</InputGrid>
							<br />
							<Button>{t.resetPassword}</Button>
						</form>
					</ContentCard>
				</>
			) : (
				<></>
			)}
			{successMsg && <CmsComponent structure={resetPasswordSuccess} />}
		</Main>
	);
};

export const resetPasswordRouting: Routing = {
	path(urls) {
		return `${urls.resetPassword}/:customerId/:code`;
	},
	component: ResetPasswordPage,
	requiresAuth: false,
};
